import { useCallback } from "react"
import type { EmailContact } from "@stockline/shared/types/communications/email-contact"
import { EMAIL_CONTACT_TABLE } from "../../../lib/offline/resources/communications/email-contact/db"
import { useAISync } from "../../ai/providers/AISyncProvider"
import type { OptimisticRollback } from "./useSmsChatMutations"

export interface UpdateEmailContactInput {
  aiContext?: string | null
  aiAnalysisEnabled?: boolean
}

export function useEmailContactMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const updateContact = useCallback(
    async (id: string, input: UpdateEmailContactInput): Promise<void> => {
      const existing = await storage.get<EmailContact>(EMAIL_CONTACT_TABLE, id)
      if (!existing) return

      const updated: EmailContact = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(EMAIL_CONTACT_TABLE, updated)
      await changeQueue.enqueue("email-contacts", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("email-contacts")
    },
    [storage, changeQueue, engine]
  )

  const setAiAnalysisEnabledOptimistic = useCallback(
    async (
      id: string,
      aiAnalysisEnabled: boolean
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<EmailContact>(EMAIL_CONTACT_TABLE, id)
      if (!existing || existing.aiAnalysisEnabled === aiAnalysisEnabled)
        return null

      const previousValue = existing.aiAnalysisEnabled
      const previousSyncId = existing.syncId ?? 0
      await storage.put<EmailContact>(EMAIL_CONTACT_TABLE, {
        ...existing,
        aiAnalysisEnabled,
      })
      await changeQueue.enqueue("email-contacts", {
        op: "update",
        id,
        data: { aiAnalysisEnabled },
      })
      void engine.pushChanges("email-contacts")

      return async () => {
        const current = await storage.get<EmailContact>(EMAIL_CONTACT_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<EmailContact>(EMAIL_CONTACT_TABLE, {
          ...current,
          aiAnalysisEnabled: previousValue,
        })
      }
    },
    [storage, changeQueue, engine]
  )

  const createContact = useCallback(
    async (id: string, data: Partial<EmailContact>): Promise<void> => {
      const now = new Date().toISOString()
      const contact: EmailContact = {
        id,
        companyId: data.companyId ?? "",
        emailAddress: data.emailAddress ?? null,
        domain: data.domain ?? null,
        emailAccountId: data.emailAccountId ?? null,
        aiContext: data.aiContext ?? null,
        waitForExistingWorkflow: data.waitForExistingWorkflow ?? false,
        aiAnalysisEnabled: data.aiAnalysisEnabled ?? true,
        deletedAt: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
      }

      await storage.put(EMAIL_CONTACT_TABLE, contact)
      await changeQueue.enqueue("email-contacts", {
        op: "create",
        id,
        data: {
          emailAddress: contact.emailAddress,
          domain: contact.domain,
          emailAccountId: contact.emailAccountId,
          aiContext: contact.aiContext,
          aiAnalysisEnabled: contact.aiAnalysisEnabled,
        },
      })
      void engine.pushChanges("email-contacts")
    },
    [storage, changeQueue, engine]
  )

  return {
    updateContact,
    setAiAnalysisEnabledOptimistic,
    createContact,
  }
}
